import { inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxConfirmModalData,
  OnyxDropdownOptionsSource,
  OnyxOptionsGroup,
  OnyxPointOfInterestAddress,
} from '@onyx/angular';
import {
  ActionHelper,
  OnyxAddressType,
  OnyxConfirmModalComponent,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import { map } from 'rxjs';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../../common/helpers/navigation.helper';
import type { HelperOptions } from '../../../../../common/interfaces/utilities/helper-options';
import { XlsxService } from '../../../../common/services/xlsx.service';
import type { PointOfInterestModalData } from '../../point-of-interest-modal/point-of-interest-modal.component';
import { PointOfInterestModalComponent } from '../../point-of-interest-modal/point-of-interest-modal.component';
import { getPointsOfInterestExportData } from '../constants/points-of-interest-export-schema';
import { POINTS_OF_INTEREST_XLSX_TEMPLATE } from '../constants/points-of-interest-xlsx-template';
import { PointsOfInterestRoute } from '../enums/points-of-interest-route';
import type { SimplifiedPointOfInterest } from '../interfaces/point-of-interest';
import { PointsOfInterestService } from '../services/points-of-interest.service';
import type { PointOfInterest } from './../interfaces/point-of-interest';

export const POINT_OF_INTEREST_HEADER_ACTIONS = ['edit', 'share'] as const;

@Service()
export class PointOfInterestHelper {
  private readonly router = inject(Router);
  private readonly pointsOfInterestService = inject(PointsOfInterestService);
  private readonly toastService = inject(OnyxToastService);
  private readonly modalService = inject(OnyxModalService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly xlsxService = inject(XlsxService);

  private readonly I18N = 'pointsOfInterest';

  public getPointsOfInterestSource(): OnyxDropdownOptionsSource<PointOfInterest> {
    return {
      list: (query, pagination) =>
        this.pointsOfInterestService.searchPointsOfInterest(query, {
          ...pagination,
          type: 'all',
        }),
      get: (uuid) =>
        this.pointsOfInterestService.getPointOfInterest(uuid).pipe(
          map((pointOfInterest) => ({
            name: pointOfInterest.name,
            value: pointOfInterest,
            leftFlag: pointOfInterest.address.countryCode,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getOptions(
    pointOfInterest: PointOfInterest,
    options: HelperOptions,
    exclude: readonly (typeof POINT_OF_INTEREST_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    return [
      {
        id: '0.default',
        options: !exclude.includes('edit')
          ? [
              {
                name: `${this.translateService.instant('labels.edit')}...`,
                value: () => this.edit(pointOfInterest.uuid, options),
                leftIcon: { name: 'edit-outlined', size: 20 },
              } as const,
            ]
          : [],
      },
      {
        id: '1.actions',
        options: !exclude.includes('share')
          ? [
              {
                name: 'labels.share',
                value: () => this.share(pointOfInterest),
                leftIcon: { name: 'share-outlined', size: 20 },
              } as const,
            ]
          : [],
      },
      {
        id: '2.delete',
        options: [
          {
            name: 'labels.export',
            value: () => this.export(pointOfInterest),
            leftIcon: { name: 'export-outlined', size: 20 },
          } as const,
          ...(!pointOfInterest.isUsed
            ? [
                {
                  name: 'labels.delete',
                  value: () => this.delete(pointOfInterest, options),
                  leftIcon: { name: 'delete-outlined' as const, size: 20 },
                  leftIconColor: 'red' as const,
                },
              ]
            : []),
        ],
      },
    ].filter((group) => group.options.length);
  }

  public openModal(pointOfInterest: PointOfInterestModalData): void {
    this.modalService.open<PointOfInterestModalData>(
      PointOfInterestModalComponent,
      pointOfInterest,
    );
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      PointsOfInterestRoute.EDIT_POINT_OF_INTEREST.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public share(pointOfInterest: PointOfInterest): void {
    ActionHelper.share(
      PointsOfInterestRoute.POINT_OF_INTEREST_CARD.replace(
        ':uuid',
        pointOfInterest.uuid,
      ),
      this.toastService,
    );
  }

  public delete(
    pointOfInterest: PointOfInterest,
    options: HelperOptions,
  ): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant(`${this.I18N}.delete.heading`, {
          pointOfInterest: pointOfInterest.name,
        }),
        message: `${this.I18N}.delete.message`,
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () =>
          this.pointsOfInterestService.deletePointOfInterest(
            pointOfInterest.uuid,
          ),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.pointDeleted`,
        });
        CommonHelper.handleOptions(pointOfInterest, options);
      });
  }

  private export(pointOfInterest: PointOfInterest): void {
    this.xlsxService.export(
      {
        template: POINTS_OF_INTEREST_XLSX_TEMPLATE,
        name: pointOfInterest.name,
      },
      getPointsOfInterestExportData(pointOfInterest, this.injector),
    );
  }

  public static getAddress(
    pointOfInterest: SimplifiedPointOfInterest,
  ): OnyxPointOfInterestAddress {
    return {
      ...pointOfInterest.address,
      type: OnyxAddressType.POINT_OF_INTEREST,
      poiUuid: pointOfInterest.uuid,
      poiName: pointOfInterest.name,
    };
  }

  public static isPointOfInterestType(
    pointOfInterest: SimplifiedPointOfInterest | PointOfInterest,
  ): pointOfInterest is PointOfInterest {
    return 'businessHours' in pointOfInterest;
  }
}
